import { Button } from "components/DesignSystem/Button/Button";
import { actionIcons } from "components/HomeActionItems/ActionItemRow";
import { ReviewAndPayIcon } from "components/icons/ReviewAndPayIcon";
import {
  CHAT_CARD_TYPE,
  ESIGN_REQUEST_CUSTOM_TYPE,
  MERCHANT_EMAIL_REQUEST_CUSTOM_TYPE,
  OPEN_ITEM_CUSTOM_TYPE,
  REQUESTED_ANSWERS_CUSTOM_TYPE,
  REQUESTED_DFT_REVIEW_TYPE,
  REQUESTED_DOCUMENTS_CUSTOM_TYPE,
  REQUESTED_INFO_CUSTOM_TYPE,
  SENSITIVE_DATA_CUSTOM_TYPE,
  PRICED_TICKET_CARD,
  ACCOUNTANT_ACCESS_REQUEST_CARD_TYPE,
} from "constants/chatType";
import { DEPRECATED, REQUESTED } from "constants/sensitiveData";
import { useDispatch } from "react-redux";
import Archive from "static/images/Archive.svg";
import ChatInfoSmallRedirect from "static/images/ChatInfoSmallRedirect.svg";
import { setMessageForStatusUpdate } from "store/slices/chat";
import { setScrollToMessageId } from "store/slices/messageToScrollTo";
import { MessageResponse } from "stream-chat";
import { DefaultStreamChatGenerics } from "stream-chat-react/dist/types/types";
import { CustomData } from "types/Models/reviewDocumentAndBalancingPayment";
import { PendingActionCardCTAS } from "./PendingActionCardCTAS";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { pluralize } from "utils/pluralize";
import { TICKET_PAID, TICKET_PAYMENT } from "constants/ticket";

export const PendingActionCard = ({
  message,
  groupId,
  completed,
  onStatusUpdateClick,
}: {
  message: MessageResponse<DefaultStreamChatGenerics> & {
    custom_data: CustomData;
  };
  groupId: string;
  completed?: boolean;
  onStatusUpdateClick?: (
    message: MessageResponse<DefaultStreamChatGenerics> & {
      custom_data: CustomData;
    }
  ) => void;
}) => {
  const dispatch = useDispatch();
  const { isAdmin } = useRoleBasedView();

  const handleStatusUpdateClick = () => {
    if (onStatusUpdateClick) {
      onStatusUpdateClick(message);
      return;
    }
    dispatch(setMessageForStatusUpdate(message));
  };

  const getPricedTicketDetails = () => {
    if (message.custom_data.card_status === TICKET_PAYMENT) {
      return "Ticket payment pending";
    }
    if (message.custom_data.card_status === TICKET_PAID) {
      return "Payment received for ticket";
    }
    return "";
  };

  let title = "";
  let subtitle = "";

  switch (message.custom_type) {
    case CHAT_CARD_TYPE: {
      title = message.custom_data.header;
      subtitle = "Review";
      break;
    }

    case OPEN_ITEM_CUSTOM_TYPE: {
      title = `Need clarifications`;
      subtitle = "Open items";
      break;
    }

    case REQUESTED_DOCUMENTS_CUSTOM_TYPE: {
      title = "Documents";
      subtitle = "Requested documents";
      break;
    }

    case REQUESTED_INFO_CUSTOM_TYPE: {
      title = "Information";
      subtitle = "Requested information";
      break;
    }

    case ESIGN_REQUEST_CUSTOM_TYPE: {
      title = "E-sign";
      subtitle = "Requested sign";
      break;
    }
    case SENSITIVE_DATA_CUSTOM_TYPE: {
      title = "Requested " + message.custom_data.header;
      subtitle = "Requested sensitive data";
      break;
    }

    case PRICED_TICKET_CARD: {
      title = getPricedTicketDetails();
      subtitle = message.custom_data.header + " (Priced ticket)";
      break;
    }

    case REQUESTED_ANSWERS_CUSTOM_TYPE: {
      const totalQuestions =
        message?.custom_data?.metadata?.requested_answers.length;

      title =
        typeof totalQuestions === "number"
          ? `${totalQuestions} questions pending`
          : "Questions pending";
      subtitle = "Answers requested";
      break;
    }

    case REQUESTED_DFT_REVIEW_TYPE: {
      const isApproved = message?.custom_data?.metadata?.is_approved;
      const isRejected = message?.custom_data?.metadata?.is_approved === false;

      if (isApproved) {
        title = "Filing draft was approved";
      } else if (isRejected) {
        title = "Filing draft was rejected";
      } else {
        title = "Filing draft pending review";
      }
      subtitle = "Filing draft review";
      break;
    }

    case ACCOUNTANT_ACCESS_REQUEST_CARD_TYPE: {
      title = message.custom_data.header;
      subtitle = "Accountant access request";
      break;
    }

    case MERCHANT_EMAIL_REQUEST_CUSTOM_TYPE: {
      const emailCountText = pluralize(
        message.custom_data.metadata?.merchant_count || 0,
        "Email address ",
        "Email addresses were "
      );
      const emailReqStatusText =
        message.card_status === REQUESTED ? "requested" : "submitted";

      title = emailCountText + emailReqStatusText;
      subtitle = "Email address request";
      break;
    }

    default:
      return null;
  }

  return (
    <div className="t-flex t-items-center t-justify-between t-gap-2 t-w-full">
      <div className="t-flex t-gap-2 t-items-center t-flex-1">
        <div className="t-relative t-min-h-8 t-min-w-8 t-max-h-8 t-max-w-8 t-border t-border-solid t-border-neutral-0 t-rounded-full t-flex t-items-center t-justify-center">
          {actionIcons[message.custom_type] || <ReviewAndPayIcon />}
        </div>
        <div>
          <div
            className="t-flex t-items-center t-text-subtext-sm t-text-text-100"
            role="button"
            onClick={() => dispatch(setScrollToMessageId(message.id))}
          >
            <span className="t-line-clamp-1">{title}</span>
            <img src={ChatInfoSmallRedirect} alt="" />
          </div>
          <div className="t-text-caption t-text-text-30 t-line-clamp-1">
            {subtitle}
          </div>
        </div>
      </div>
      <div className="t-min-w-fit t-flex t-gap-2">
        {message.card_status === DEPRECATED ? (
          <Button onClick={handleStatusUpdateClick}>Reopen</Button>
        ) : (
          <>
            {isAdmin && message.card_status === REQUESTED && (
              <div>
                <Button
                  size="small"
                  customType="icon"
                  onClick={handleStatusUpdateClick}
                >
                  <img src={Archive} alt="Archive" />
                </Button>
              </div>
            )}
            <div>
              <PendingActionCardCTAS
                message={message}
                groupId={groupId}
                completed={completed}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
